import type {SxProps, Theme} from "@mui/material";
import {ELLIPSIS} from "../../utils/str.utils";

const CATEGORY_CHIP_ARROW_WIDTH = 16;

const CATEGORY_CHIP_LABEL_PADDING_X = 10;
const CATEGORY_CHIP_ELLIPSIS_START = 3;
const CATEGORY_CHIP_ELLIPSIS_END = 6;
const CATEGORY_CHIP_LABEL_FONT = '400 13px Roboto, Helvetica, Arial, sans-serif';
const CATEGORY_CHIP_FALLBACK_CHAR_WIDTH = 5;

const getCategoryChipMeasureContext = (() => {
    let ctx: CanvasRenderingContext2D | null | undefined;

    return (): CanvasRenderingContext2D | null => {
        if (ctx !== undefined) return ctx;
        if (typeof document === 'undefined') {
            ctx = null;
            return null;
        }
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) context.font = CATEGORY_CHIP_LABEL_FONT;
        ctx = context;
        return ctx;
    };
})();

function measureCategoryChipLabelWidth(text: string): number {
    const ctx = getCategoryChipMeasureContext();
    if (ctx) return ctx.measureText(text).width;
    return text.length * CATEGORY_CHIP_FALLBACK_CHAR_WIDTH;
}

function categoryChipLabelMaxWidth(chipWidthPx: number): number {
    return chipWidthPx - (2 * CATEGORY_CHIP_LABEL_PADDING_X);
}

function categoryChipTextFits(text: string, chipWidthPx: number): boolean {
    return measureCategoryChipLabelWidth(text) <= categoryChipLabelMaxWidth(chipWidthPx);
}

export function doCategoryChipEllipsis(s: string, chipWidthPx: number): [string, boolean] {
    if (categoryChipTextFits(s, chipWidthPx)) return [s, false];
    if (s.length <= CATEGORY_CHIP_ELLIPSIS_START + CATEGORY_CHIP_ELLIPSIS_END) return [s, false];

    const truncated = `${s.substring(0, CATEGORY_CHIP_ELLIPSIS_START)}${ELLIPSIS}${s.substring(s.length - CATEGORY_CHIP_ELLIPSIS_END)}`;
    return [truncated, true];
}

export function getCategoryChipSx(chipMaxWidth?: number): SxProps<Theme> {
    return {
        mt: '2px',
        mb: '2px',
        flexShrink: 1,
        ...(chipMaxWidth != null ? {maxWidth: chipMaxWidth} : {}),
        '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    };
}

export const categoryChipArrowStyle = {
    width: CATEGORY_CHIP_ARROW_WIDTH,
    height: CATEGORY_CHIP_ARROW_WIDTH,
    flexShrink: 0,
} as const;
